import { FinancingApi, Provider, resolveProviderEnum } from '@servicetitan/financing-api-client';
import { useDependencies } from '@servicetitan/react-ioc';
import { useQuery } from '@tanstack/react-query';
import {
    SetupDataStore,
    useProviderConfig,
    SecondLookServiceDegradedStore,
    useSecondLookIntegration,
} from '@servicetitan/financing-workflow-shared';
import { SelectField } from '@servicetitan/anvil2-react-hook-form';
import { CSSProperties, useMemo } from 'react';

interface IndustryTypeComboboxProps {
    fieldPrefix?: string;
    style?: CSSProperties;
}

export const IndustryTypeCombobox = ({ fieldPrefix = '', style }: IndustryTypeComboboxProps) => {
    const [apiClient] = useDependencies(FinancingApi);
    const { setupData } = SetupDataStore.useStore();
    const { setIsSecondLookServiceDegraded } = SecondLookServiceDegradedStore.useStore();
    const { additionalApplicantFields = [] } = useProviderConfig();
    const turnsIntegration = useSecondLookIntegration();

    const fieldName = `${fieldPrefix}.industryType`;

    // When second look is enabled, secondLookProvider is always non-null:
    // - All flows: isEnabled requires secondLookProvider non-null (technician falls back to 'Turns')
    const catalogProvider = turnsIntegration.isEnabled
        ? resolveProviderEnum(turnsIntegration.secondLookProvider!)
        : ((setupData?.provider as Provider) ?? Provider.None);

    const buId = setupData?.buId ?? undefined;

    const { data } = useQuery({
        queryKey: ['industryTypes', catalogProvider, buId],
        queryFn: async () => {
            try {
                const response = await apiClient.providersGetCatalog({
                    provider: catalogProvider,
                    catalogName: 'Industries',
                    businessUnitId: buId,
                });
                return response.data;
            } catch {
                setIsSecondLookServiceDegraded(true);
                return { items: [] };
            }
        },
        enabled: additionalApplicantFields.includes('jobDetails') || turnsIntegration.isEnabled,
    });

    // Convert to SelectFieldOption format
    const options = useMemo(() => {
        const validItems =
            data?.items?.filter(item => item.key != null && item.displayName != null) ?? [];
        return validItems.map(item => ({
            id: item.key!,
            label: item.displayName!,
        }));
    }, [data?.items]);

    return data != null ? (
        <SelectField
            name={fieldName}
            options={options}
            label="Improvement Type"
            required
            style={style}
        />
    ) : null;
};
